import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Crown } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'

interface FutureChampionExistedModalProps {
  eventName: string
  currentChoice: string
  onKeepClick: () => void
  onChangeClick: () => void
}

function normalizeChoiceDisplay(raw: string): string {
  if (typeof raw !== 'string') return String(raw)
  return raw.replace(/^[\s"{]+|[\s"}]+$/g, '').trim()
}

const FutureChampionExistedModal: React.FC<FutureChampionExistedModalProps> = ({
  eventName,
  currentChoice,
  onKeepClick,
  onChangeClick,
}) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    setIsOpen(true)
  }, [])

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="blue-sky-card rounded-3xl p-4 sm:p-6 lg:p-8 w-full max-w-md mx-4 shadow-2xl max-h-[80vh] overflow-y-auto">

        <div className="text-center mb-4 sm:mb-5">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 mb-3">
            <Crown className="w-6 h-6 text-yellow-400" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-white mb-1">{eventName}</h2>
          <p className="text-white/80 font-semibold text-sm sm:text-base">
            {t('predictions.alreadyPlayed')}
          </p>
        </div>

        <div className="bg-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-5 mb-5">
          <p className="text-white/70 text-xs sm:text-sm mb-1">
            {t('futureChampion.yourPrediction') || 'Your prediction'}
          </p>
          <p className="text-white font-bold text-lg sm:text-xl">{normalizeChoiceDisplay(currentChoice)}</p>
        </div>

        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={() => {
              onKeepClick()
              setIsOpen(false)
            }}
            className="flex-1 px-4 py-2.5 sm:px-6 sm:py-3 bg-primary hover:opacity-90 active:scale-95 text-primary-foreground rounded-xl transition-all font-semibold text-sm sm:text-base"
          >
            {t('predictions.keep')}
          </button>
          <button
            onClick={() => {
              onChangeClick()
              setIsOpen(false)
            }}
            className="flex-1 px-4 py-2.5 sm:px-6 sm:py-3 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all font-semibold text-sm sm:text-base"
          >
            {t('predictions.change')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default FutureChampionExistedModal
